import React from 'react';
import { cn } from '@/lib/cn';

export type MiniTagSeverity = 'low' | 'medium' | 'high' | 'highest' | 'positive' | 'neutral';

export interface MiniTagProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Severity determines colors and default label */
  severity?: MiniTagSeverity;
  /** Optional custom label; if omitted, uses the severity label */
  children?: React.ReactNode;
  /** Optional className for the label text */
  labelClassName?: string;
}

const DEFAULT_LABEL: Record<MiniTagSeverity, string> = {
  low: 'Missing info',
  medium: 'Non-hazardous',
  high: 'Hazardous',
  highest: 'Immediately hazardous',
  positive: 'Active',
  neutral: 'Draft',
};

const STYLES: Record<MiniTagSeverity, { container: string; text: string }> = {
  low: { container: 'bg-caution-low', text: 'text-caution' },
  medium: { container: 'bg-caution-mid', text: 'text-caution-mid' },
  high: { container: 'bg-alert-low', text: 'text-alert' },
  highest: { container: 'bg-alert-mid', text: 'text-alert-mid' },
  positive: { container: 'bg-success-low', text: 'text-success' },
  neutral: { container: 'bg-tertiary', text: 'text-primary' },
};

/**
 * MiniTag - small pill for severity/status labels.
 * Matches Figma mini tag variants.
 */
export function MiniTag({
  severity = 'low',
  children,
  className,
  labelClassName,
  ...props
}: MiniTagProps) {
  const label = children ?? DEFAULT_LABEL[severity];
  const styles = STYLES[severity];

  return (
    <div
      className={cn(
        'inline-flex items-center gap-0 px-2 py-1 rounded-full overflow-hidden',
        styles.container,
        styles.text,
        className,
      )}
      {...props}
    >
      <span className={cn('text-caption font-normal whitespace-nowrap', labelClassName)}>
        {label}
      </span>
    </div>
  );
}

export default MiniTag;
